import { Schema, type SchemaInferOutput } from '@/shared/lib';

import { formatMessageDisplayDateTime } from '../../lib';
import {
  MessageAttachmentType,
  MessageBorderStyleType,
  MessageDeliveryStatus,
  MessageType,
} from '../enums';

const _getAttachmentExtension = (attachmentOriginalName: string): string => {
  const dotIndex = attachmentOriginalName.lastIndexOf('.');
  return dotIndex > 0 ? attachmentOriginalName.slice(dotIndex) : '';
};

const MessageAttachmentSchema = Schema.structures.pipe(
  Schema.structures.object({
    id: Schema.schemas.ID(),
    messageId: Schema.schemas.ID(),
    originalName: Schema.schemas.string(),
    size: Schema.schemas.number(),
    contentType: Schema.schemas.string(),
    type: Schema.schemas.picklist([
      MessageAttachmentType.IMAGE,
      MessageAttachmentType.FILE,
    ] as const),
    url: Schema.schemas.string(),
    createdAt: Schema.schemas.dateFromString(),
  }),
  Schema.structures.transform((input) => ({
    ...input,
    extension: _getAttachmentExtension(input.originalName),
  })),
);

const MessageReplyPreviewSchema = Schema.structures.object({
  id: Schema.schemas.ID(),
  senderId: Schema.structures.nullish(Schema.schemas.ID()),
  content: Schema.structures.nullish(Schema.schemas.string()),
  type: Schema.structures.nullish(Schema.schemas.string()),
  deletedAt: Schema.structures.nullish(Schema.schemas.dateFromString()),
});

const MessageReactionSummarySchema = Schema.structures.object({
  emoji: Schema.schemas.string(),
  count: Schema.schemas.number(),
  reactedByCurrentUser: Schema.structures.optional(Schema.schemas.boolean(), false),
});

const MessageInfoSchema = Schema.structures.pipe(
  Schema.structures.object({
    id: Schema.schemas.ID(),
    chatId: Schema.schemas.ID(),
    senderId: Schema.schemas.ID(),
    clientMessageId: Schema.structures.nullish(Schema.schemas.ID()),
    content: Schema.structures.nullish(Schema.schemas.string()),
    createdAt: Schema.schemas.dateFromString(),
    updatedAt: Schema.structures.nullish(Schema.schemas.dateFromString()),
    deletedAt: Schema.structures.nullish(Schema.schemas.dateFromString()),
    readAt: Schema.structures.nullish(Schema.schemas.dateFromString()),
    type: Schema.schemas.picklist([
      MessageType.TEXT,
      MessageType.IMAGE,
      MessageType.FILE,
      MessageType.EVENT,
    ] as const),
    byCurrentUser: Schema.schemas.boolean(),
    attachments: Schema.structures.nullish(Schema.schemas.array(MessageAttachmentSchema)),
    replyToMessageId: Schema.structures.nullish(Schema.schemas.ID()),
    replyTo: Schema.structures.nullish(MessageReplyPreviewSchema),
    reactions: Schema.structures.nullish(Schema.schemas.array(MessageReactionSummarySchema)),
    deliveryStatus: Schema.structures.optional(
      Schema.schemas.picklist([
        MessageDeliveryStatus.PENDING,
        MessageDeliveryStatus.SENDING,
        MessageDeliveryStatus.FAILED,
        MessageDeliveryStatus.SENT,
      ] as const),
      MessageDeliveryStatus.SENT,
    ),
    borderStyle: Schema.structures.optional(
      Schema.schemas.picklist([
        MessageBorderStyleType.START,
        MessageBorderStyleType.END,
        MessageBorderStyleType.CENTER,
      ] as const),
      MessageBorderStyleType.CENTER,
    ),
  }),

  Schema.structures.transform((input) => ({
    ...input,
    content: input.content ?? '',
    attachments: input.attachments ?? [],
    hasAttachments: (input.attachments ?? []).length !== 0,
    isWasRead: !!input.readAt,
    isWasUpdate: !!input.updatedAt,
    lastModifyDate: input.updatedAt ?? input.createdAt,
    formattedLastModifyDate: formatMessageDisplayDateTime(input.updatedAt ?? input.createdAt),
    formattedReadDateTime: formatMessageDisplayDateTime(input.readAt),
    replyTo: input.replyTo
      ? {
          ...input.replyTo,
          content: input.replyTo.content ?? '',
        }
      : null,
    reactions: input.reactions ?? [],
    deliveryStatus: input.deliveryStatus,
  })),
);

type MessageInfoSchemaType = SchemaInferOutput<typeof MessageInfoSchema>;

export { MessageInfoSchema, type MessageInfoSchemaType };
